'use client';

import { createTRPCReact } from '@trpc/react-query';
import { httpBatchLink } from '@trpc/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import type { AppRouter } from '@slikerr/api/trpc';
import { makeAuthFetch } from './auth-fetch';
import { useAuthInternal } from './auth-context';
import { API_URL } from './api-url';
import { clientVersionHeaders } from './client-version';

export const trpc = createTRPCReact<AppRouter>();

/**
 * Inner component — must render inside AuthProvider so useAuthInternal works.
 * Stable instances: QueryClient + trpcClient are created once per mount.
 */
function TRPCInner({ children }: { children: ReactNode }) {
  const auth = useAuthInternal();

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: { queries: { retry: false, staleTime: 30_000 } },
      }),
  );

  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        httpBatchLink({
          url: `${API_URL}/trpc`,
          // H1: X-App-Version / X-Platform on every request. `headers` runs per
          // request and merges into the init the custom fetch below receives,
          // which rebuilds `new Headers(init.headers)` and only sets
          // Authorization - so these survive both the first call and the
          // post-refresh retry.
          headers: clientVersionHeaders,
          // Custom fetch: attaches Bearer, handles transparent 401→refresh→retry.
          // IMPORTANT: use auth.getAccessToken() (ref-backed, stable callback) rather
          // than auth.accessToken directly — the trpcClient is created once in
          // useState and its closure would otherwise capture the initial null snapshot.
          // makeAuthFetch attaches the Authorization header (and handles
          // 401→refresh→retry). AUTHORIZATION specifically has no headers()
          // callback — keeping a single source of truth for it avoids a
          // duplicate header on every request. The `headers` option above is
          // for the version headers only and never touches Authorization.
          fetch: makeAuthFetch({
            getAccessToken: () => auth.getAccessToken(),
            getRefreshToken: () => auth.getRefreshToken(),
            onRefreshed: (at, rt) => auth._onRefreshed(at, rt),
            onRefreshFailed: () => auth._onRefreshFailed(),
            // Ref-backed on purpose. `auth.impersonation` here would be the
            // `null` captured when this closure was built in useState, so the
            // never-refresh-while-impersonating guard would silently never
            // fire — and no test of makeAuthFetch alone would catch it.
            isImpersonating: () => auth._isImpersonating(),
            onImpersonationExpired: () => auth._onImpersonationExpired(),
          }),
        }),
      ],
    }),
  );

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
}

/**
 * TRPCProvider — wrap your app (inside AuthProvider) with this component.
 */
export function TRPCProvider({ children }: { children: ReactNode }) {
  return <TRPCInner>{children}</TRPCInner>;
}
